import { TILES_CACHE, TILES_DOWNLOAD_URL, TILES_URL } from './basemap'

/**
 * Das Kachelarchiv für die Reise ablegen, prüfen und wieder löschen.
 *
 * Das Archiv ist einige hundert Megabyte gross. Es darf deshalb auf keinen
 * Fall beiläufig in den Cache wandern - nicht beim Installieren des Service
 * Workers, nicht beim ersten Blick auf die Karte. Es wandert nur dorthin, wenn
 * jemand ausdrücklich darauf drückt, und zwar an einem Ort mit WLAN und
 * nicht im Roaming.
 *
 * Gespeichert wird in der Cache API und nicht in IndexedDB, weil der Service
 * Worker die Bereichsanfragen von MapLibre direkt aus diesem Cache beantworten
 * kann (Workbox, rangeRequests). Ein Umweg über die Anwendung wäre für jede
 * einzelne Kachel ein Umweg zu viel.
 */

/** Was über das lokal abgelegte Archiv bekannt ist. */
export interface BasemapCacheState {
  /** Liegt das Archiv im Cache? */
  stored: boolean
  /** Grösse in Bytes, 0 wenn unbekannt. */
  bytes: number
}

export const KEIN_ARCHIV: BasemapCacheState = { stored: false, bytes: 0 }

/** Fortschritt eines laufenden Downloads. */
export interface DownloadProgress {
  loaded: number
  /** Gesamtgrösse laut Content-Length, 0 wenn der Server sie nicht nennt. */
  total: number
}

/**
 * Fortschritt in Prozent, auf ganze Zahlen gerundet und auf 0..100 begrenzt.
 *
 * Ohne bekannte Gesamtgrösse gibt es keinen Prozentwert; dann ist das
 * Ergebnis null und die Oberfläche zeigt nur die geladenen Megabyte.
 */
export function progressPercent(loaded: number, total: number): number | null {
  if (!(total > 0)) return null
  return Math.max(0, Math.min(100, Math.round((loaded / total) * 100)))
}

/** Bytes als Megabyte mit einer Nachkommastelle, etwa 371 MB. */
export function megabytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 MB'
  const mb = bytes / (1024 * 1024)
  return `${mb >= 100 ? Math.round(mb) : Math.round(mb * 10) / 10} MB`
}

/** Steht die Cache API zur Verfügung? Ohne sie gibt es keine Offlinekarte. */
export function cacheAvailable(): boolean {
  return typeof caches !== 'undefined'
}

/**
 * Darf nach einem gescheiterten Schreiben aufgeräumt werden?
 *
 * Nur, wenn vorher kein Archiv da war. Ein Schreiben in die Cache API ist
 * unteilbar: cache.put liest den Rumpf vollständig und ersetzt den alten
 * Eintrag erst danach. Scheitert es, liegt also entweder ein halber Eintrag da,
 * den vorher niemand hatte - der gehört weg -, oder das alte, vollständige
 * Archiv - und das ist genau die Karte, die jemand unterwegs gerade braucht.
 *
 * Der teure Fehler wird beim "Neu laden" gemacht: Netz wackelt, der Download
 * bricht bei 40 Prozent ab, und pauschales Aufräumen wirft dabei die
 * funktionierende Karte weg, und zwar dort, wo sie sich nicht neu laden lässt.
 *
 * Die Alternative - unter einem vorläufigen Schlüssel laden und erst nach
 * Erfolg umhängen - ist nicht genommen: dann lagen kurzzeitig zwei Archive von
 * je 371 MB im Speicher. Das Kontingent des Browsers ist endlich, und ein
 * Neuladen, das am selbst verdoppelten Platzbedarf scheitert, wäre derselbe
 * Fehler eine Etage höher.
 */
export function cleanupAfterFailure(hadArchive: boolean): boolean {
  return !hadArchive
}

/** Was die Bedienoberfläche für die Offlinekarte zeigen soll. */
export interface OfflineControls {
  /** Überhaupt etwas zeigen? */
  show: boolean
  /** Statt Knöpfen der Hinweis, dass dieser Browser nicht offline kann. */
  unsupported: boolean
  /** Knopf zum Laden bzw. Neuladen. */
  download: boolean
  /** Knopf zum Löschen der lokalen Kopie. */
  remove: boolean
}

export interface OfflineControlsInput {
  /** Kann dieser Browser in die Cache API schreiben? */
  cache: boolean
  /** Liegt auf dem Server ein Archiv? Aus /api/v1/config. */
  hasBasemap: boolean
  /** Liegt das Archiv lokal auf dem Gerät? */
  stored: boolean
}

/**
 * Entscheidet, welche Knöpfe es für die Offlinekarte gibt.
 *
 * Der wichtige Fall ist der unerwartete: verschwindet das Archiv auf dem Server,
 * liegen die 371 MB trotzdem weiter auf dem Gerät. Hängt die Bedienoberfläche
 * allein an hasBasemap, verschwindet mit dem Serverarchiv auch der einzige Weg,
 * die lokale Kopie loszuwerden. Was lokal liegt, muss sich also immer löschen
 * lassen - nur neu laden kann man dann nichts mehr.
 */
export function offlineControls({
  cache,
  hasBasemap,
  stored,
}: OfflineControlsInput): OfflineControls {
  if (stored) return { show: true, unsupported: false, download: cache && hasBasemap, remove: true }
  // Ohne Serverarchiv und ohne lokale Kopie gibt es nichts zu bedienen und
  // nichts zu erklären.
  if (!hasBasemap) return { show: false, unsupported: false, download: false, remove: false }
  if (!cache) return { show: true, unsupported: true, download: false, remove: false }
  return { show: true, unsupported: false, download: true, remove: false }
}

/**
 * Sieht nach, ob das Archiv schon lokal liegt.
 *
 * Die Grösse kommt aus Content-Length der abgelegten Antwort. Sie über
 * response.blob() zu messen wäre genauer, würde aber die ganze Datei anfassen
 * - für eine Anzeige zu teuer.
 */
export async function readBasemapCache(): Promise<BasemapCacheState> {
  if (!cacheAvailable()) return KEIN_ARCHIV
  try {
    const cache = await caches.open(TILES_CACHE)
    const hit = await cache.match(TILES_URL)
    if (!hit) return KEIN_ARCHIV
    return { stored: true, bytes: Number(hit.headers.get('Content-Length') ?? 0) || 0 }
  } catch (err) {
    console.warn('Kartencache nicht lesbar', err)
    return KEIN_ARCHIV
  }
}

/** Wirft das Archiv aus dem Cache. */
export async function removeBasemap(): Promise<void> {
  if (!cacheAvailable()) return
  const cache = await caches.open(TILES_CACHE)
  await cache.delete(TILES_URL)
}

/**
 * Holt das Archiv und legt es im Cache ab, mit Fortschrittsmeldung.
 *
 * Der Rumpf wird durch einen TransformStream geschleift, der nur mitzählt.
 * Ihn erst vollständig in den Speicher zu lesen und dann abzulegen wäre
 * einfacher, würde aber ein paar hundert Megabyte im Arbeitsspeicher halten -
 * auf einem Telefon der sichere Absturz.
 *
 * Abgelegt wird unter TILES_URL, geholt von TILES_DOWNLOAD_URL: derselbe
 * Server, dieselbe Datei, aber eine Adresse, an der die Service-Worker-Regel
 * vorbeigreift (siehe basemap.ts).
 *
 * Ein zweiter Durchlauf ("Neu laden") ist gefahrlos: scheitert er, bleibt das
 * bereits abgelegte Archiv liegen, statt aufgeräumt zu werden - siehe
 * cleanupAfterFailure.
 */
export async function downloadBasemap(
  onProgress: (progress: DownloadProgress) => void,
  signal?: AbortSignal,
): Promise<BasemapCacheState> {
  if (!cacheAvailable()) {
    throw new Error('Dieser Browser kann keine Karte offline speichern.')
  }

  // Ohne dauerhaften Speicher räumt der Browser den Cache bei Platzmangel
  // ohne Rückfrage weg - genau dann, wenn die Karte gebraucht wird. Eine
  // Ablehnung ist kein Grund abzubrechen, nur ein Grund es zu wissen.
  if (navigator.storage?.persist) {
    try {
      await navigator.storage.persist()
    } catch (err) {
      console.warn('Dauerhafter Speicher nicht zugesagt', err)
    }
  }

  const response = await fetch(TILES_DOWNLOAD_URL, { signal, credentials: 'omit' })
  if (!response.ok) {
    throw new Error(
      response.status === 404
        ? 'Auf dem Server liegt kein Kartenarchiv.'
        : `Der Server antwortete mit ${String(response.status)}.`,
    )
  }
  if (!response.body) throw new Error('Die Antwort des Servers hat keinen Rumpf.')

  const total = Number(response.headers.get('Content-Length') ?? 0) || 0
  let loaded = 0
  onProgress({ loaded, total })

  const counting = new TransformStream<Uint8Array, Uint8Array>({
    transform(chunk, controller) {
      loaded += chunk.byteLength
      onProgress({ loaded, total })
      controller.enqueue(chunk)
    },
  })

  // Eine eigene Antwort mit Status 200: die Cache API weigert sich, eine
  // Teilantwort abzulegen, und weiterreichen wollen wir nur die Kopfzeilen,
  // die der Service Worker für die Bereichsanfragen wirklich braucht.
  const stored = new Response(response.body.pipeThrough(counting), {
    status: 200,
    headers: kopfzeilen(response, total),
  })

  // Vor dem Schreiben festhalten, ob schon ein Archiv da ist: davon hängt ab,
  // ob nach einem Fehlschlag aufgeräumt werden darf (siehe
  // cleanupAfterFailure). Gelesen werden dabei nur Kopfzeilen, nicht die Datei.
  const vorhanden = (await readBasemapCache()).stored

  try {
    const cache = await caches.open(TILES_CACHE)
    await cache.put(TILES_URL, stored)
  } catch (err) {
    // Der erste, abgebrochene Download hinterlässt sonst einen halben Eintrag,
    // aus dem MapLibre nur Fehler bekäme. Lag vorher ein vollständiges Archiv
    // da, ist es unberührt und bleibt liegen.
    if (cleanupAfterFailure(vorhanden)) await removeBasemap().catch(() => undefined)
    if (err instanceof DOMException && err.name === 'QuotaExceededError') {
      throw new Error('Zu wenig Speicherplatz auf dem Gerät für die Karte.')
    }
    throw err
  }

  return { stored: true, bytes: total || loaded }
}

function kopfzeilen(response: Response, total: number): Headers {
  const headers = new Headers()
  headers.set('Content-Type', response.headers.get('Content-Type') ?? 'application/octet-stream')
  // Content-Length wird später als Grössenanzeige gelesen. Fällt sie aus,
  // steht dort eben 0 und die Anzeige nennt keine Grösse.
  if (total > 0) headers.set('Content-Length', String(total))
  // Accept-Ranges ist für den Client die Zusage, dass Bereichsanfragen gehen.
  headers.set('Accept-Ranges', 'bytes')
  const etag = response.headers.get('ETag')
  if (etag) headers.set('ETag', etag)
  return headers
}
